import type { CompassCardComponentProps } from '@scale/compass-ui/types';
import type {
  HubstaffMemberActionsContext,
  HubstaffMemberAction,
  HubstaffUserIdentityType,
  HubstaffMemberRole,
  HubstaffTimeTracking,
} from '@scale/genai-compass-shared/cards/hubstaff_member_actions';
import type { Column } from '@scale/compass-shared/types';
import { Flex, Callout } from '@scale/scaleui-radix';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProjectIdInput from './ProjectIdInput';
import ActionSelector from './ActionSelector';
import UserIdentityTypeSelector from './UserIdentityTypeSelector';
import UserIdentifierColumnSelector from './UserIdentifierColumnSelector';
import MemberRoleSelector from './MemberRoleSelector';
import TimeTrackingSelector from './TimeTrackingSelector';
import WorkLimitsSelector from './WorkLimitsSelector';
import ConfigurationSummary from './ConfigurationSummary';

export default function HubstaffMemberActionsCardComponent({
  context,
  updateCardContext,
  previousCardData,
}: CompassCardComponentProps<HubstaffMemberActionsContext>) {
  // Get columns from previous card
  const previousColumns: Column[] = previousCardData?.columns || [];

  const handleProjectIdChange = (projectId: string) => {
    updateCardContext({
      ...context,
      hubstaffProjectId: projectId,
      hubstaffProjectName: undefined,
    });
  };

  const handleActionChange = (action: HubstaffMemberAction) => {
    updateCardContext({
      ...context,
      action,
      // Clear action-specific fields when action changes
      memberRole: undefined,
      timeTracking: undefined,
      dailyLimit: undefined,
      weeklyLimit: undefined,
    });
  };

  const handleIdentifierColumnChange = (columnId: string) => {
    updateCardContext({
      ...context,
      userIdentifierColumn: columnId,
    });
  };

  const handleIdentityTypeChange = (identityType: HubstaffUserIdentityType) => {
    updateCardContext({
      ...context,
      userIdentityType: identityType,
    });
  };

  const handleMemberRoleChange = (role: HubstaffMemberRole) => {
    updateCardContext({
      ...context,
      memberRole: role,
    });
  };

  const handleTimeTrackingChange = (timeTracking: HubstaffTimeTracking) => {
    updateCardContext({
      ...context,
      timeTracking,
    });
  };

  const handleDailyLimitChange = (dailyLimit: number | string | undefined) => {
    updateCardContext({
      ...context,
      dailyLimit: dailyLimit !== undefined ? String(dailyLimit) : undefined,
    });
  };

  const handleWeeklyLimitChange = (weeklyLimit: number | string | undefined) => {
    updateCardContext({
      ...context,
      weeklyLimit: weeklyLimit !== undefined ? String(weeklyLimit) : undefined,
    });
  };

  return (
    <Flex direction="column" gap="4">
      <Callout.Root color="blue" size="1">
        <Callout.Icon>
          <FontAwesomeIcon icon={faInfoCircle} />
        </Callout.Icon>
        <Callout.Text>
          This card performs bulk Hubstaff member management operations on users from the previous
          card's output.
        </Callout.Text>
      </Callout.Root>

      <ProjectIdInput projectId={context.hubstaffProjectId} onChange={handleProjectIdChange} />

      <ActionSelector action={context.action} onChange={handleActionChange} />

      <UserIdentityTypeSelector
        identityType={context.userIdentityType}
        onChange={handleIdentityTypeChange}
      />

      <UserIdentifierColumnSelector
        selectedColumn={context.userIdentifierColumn}
        availableColumns={previousColumns}
        onChange={handleIdentifierColumnChange}
      />

      {context.action === 'Change Role' && (
        <MemberRoleSelector role={context.memberRole} onChange={handleMemberRoleChange} />
      )}

      {context.action === 'Change Time Tracking' && (
        <TimeTrackingSelector
          timeTracking={context.timeTracking}
          onChange={handleTimeTrackingChange}
        />
      )}

      {context.action === 'Set Work Limits' && (
        <WorkLimitsSelector
          dailyLimit={context.dailyLimit}
          weeklyLimit={context.weeklyLimit}
          onDailyLimitChange={handleDailyLimitChange}
          onWeeklyLimitChange={handleWeeklyLimitChange}
        />
      )}

      <ConfigurationSummary context={context} availableColumns={previousColumns} />
    </Flex>
  );
}
